import { useRef, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  FormHelperText,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import { baseURL } from "store/services/base.config";
import { useTranslation } from "react-i18next";

export default function DocumentUploader({ label, file, onFileChange, error }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleFileSelect = (e) => {
    if (e.target.files?.length > 0) {
      onFileChange(e.target.files[0]); // File object
      e.target.value = ""; // allow selecting same file again
    }
  };

  const renderFilePreview = () => {
    if (!file) return null;

    // If file is a string (path), treat it as a path to an image or pdf
    if (typeof file === "string") {
      const fileUrl = file.startsWith("http")
        ? file
        : `${baseURL}${file.startsWith("/") ? "" : "/"}${file}`;
      return file.toLowerCase().split("?")[0].endsWith(".pdf") ? (
        <PictureAsPdfIcon sx={{ fontSize: 40, color: "#d32f2f" }} />
      ) : (
        <img
          src={fileUrl}
          alt="preview"
          style={{ height: 80, objectFit: "contain", borderRadius: 6 }}
        />
      );
    }

    // For regular file objects
    if (file.type.includes("pdf")) {
      return <PictureAsPdfIcon sx={{ fontSize: 40, color: "#d32f2f" }} />;
    }

    return (
      <img
        src={URL.createObjectURL(file)} // File object, create object URL
        alt="preview"
        style={{ height: 80, objectFit: "contain", borderRadius: 6 }}
      />
    );
  };

  return (
    <Box
      sx={{
        border: "1px dashed",
        borderColor: error
          ? "error.main"
          : isDragActive
          ? "#1976d2"
          : "#9ca3af",
        borderRadius: "10px",
        padding: 2,
        height: 220,
        textAlign: "center",
        cursor: "pointer",
        transition: "0.25s",
        "&:hover": {
          borderColor: error ? "error.main" : "#1976d2",
          backgroundColor: "#f3f8ff",
        },
      }}
      component="label"
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragActive(true);
      }}
      onDragLeave={() => setIsDragActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragActive(false);
        const droppedFile = e.dataTransfer?.files?.[0];
        if (droppedFile) {
          onFileChange(droppedFile);
          if (inputRef.current) {
            inputRef.current.value = "";
          }
        }
      }}
    >
      <Typography fontWeight={600} mb={1}>
        {label} *
      </Typography>

      {!file ? (
        <Stack alignItems="center" spacing={1} mt={3}>
          <CloudUploadIcon sx={{ fontSize: 38, color: "#6b7280" }} />
          <Typography variant="body2" color="text.secondary">
            {isDragActive
              ? t("auth.common.isDragActive")
              : t("auth.common.isNotDragActive")}
          </Typography>
        </Stack>
      ) : (
        <Stack alignItems="center" spacing={1}>
          {renderFilePreview()}

          <Typography fontSize={12} sx={{ maxWidth: 160, textAlign: "center" }}>
            {typeof file === "string" ? file.split("/").pop() : file.name}
          </Typography>

          <IconButton
            size="small"
            color="error"
            onClick={() => {
              onFileChange(null);
              if (inputRef.current) {
                inputRef.current.value = "";
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      )}

      <input
        type="file"
        hidden
        accept="image/*,.pdf"
        ref={inputRef}
        onChange={handleFileSelect}
      />
      {error ? (
        <FormHelperText error sx={{ mt: 1 }}>
          {error}
        </FormHelperText>
      ) : null}
    </Box>
  );
}
